import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog } from "@/components/rent/core/ui/Dialog";
import { Button } from "@/components/rent/core/ui/Button";
import { Input, Field, Label } from "@/components/rent/core/ui/Input";
import { Avatar } from "@/components/rent/core/Avatar";
import { fmtINR, fmtDate } from "@/components/rent/core/format";
import { api } from "@/components/rent/core/api";
import { SplitPaymentEditor } from "./SplitPaymentEditor";
import { useRecordPayment } from "./useRecordPayment";
import { fireConfetti } from "./animations";

interface Tenant {
  id: string;
  name: string;
  phone: string;
}
interface PendingDue {
  id: string;
  agreement_id: string;
  due_date: string;
  amount_paise: number;
  late_fee_per_day_paise?: number | null;
  late_fee_grace_days?: number;
  late_fee_cap_paise?: number | null;
  property_name: string;
  tenant_name: string;
}

export function RecordPaymentWizard({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSuccess: () => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [pendingDues, setPendingDues] = useState<PendingDue[]>([]);
  const [selectedDue, setSelectedDue] = useState<PendingDue | null>(null);

  useEffect(() => {
    if (!open) return;
    api
      .get<{ items: Tenant[] }>("/py-api/v1/tenants?size=200")
      .then((r) => setTenants(r.items ?? []))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!tenantId) return;
    api
      .get<{ items: PendingDue[] }>(`/py-api/v1/payments/pending?tenant_id=${tenantId}`)
      .then((r) => setPendingDues(r.items ?? []))
      .catch(() => {});
  }, [tenantId]);

  function reset() {
    setStep(1);
    setTenantId(null);
    setPendingDues([]);
    setSelectedDue(null);
    setTenantSearch("");
  }

  const filteredTenants = tenantSearch
    ? tenants.filter(
        (t) =>
          t.name.toLowerCase().includes(tenantSearch.toLowerCase()) ||
          t.phone.includes(tenantSearch),
      )
    : tenants;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
      title="Record Payment"
    >
      <motion.div
        key={step}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="space-y-4"
      >
        <Stepper current={step} />

        {step === 1 && (
          <div>
            <div className="text-sm text-[var(--cp-ink-3)] mb-2">Pick the tenant</div>
            <input
              type="search"
              placeholder="Search by name or phone…"
              className="cp-input w-full p-2 text-sm"
              value={tenantSearch}
              onChange={(e) => setTenantSearch(e.target.value)}
            />
            <ul className="mt-3 max-h-64 overflow-y-auto divide-y divide-[var(--cp-line)]">
              {filteredTenants.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center gap-3 p-2 hover:bg-[var(--cp-surface-2)] cursor-pointer"
                  onClick={() => {
                    setTenantId(t.id);
                    setStep(2);
                  }}
                >
                  <Avatar name={t.name} size={32} />
                  <div className="flex-1">
                    <div className="text-sm">{t.name}</div>
                    <div className="text-xs text-[var(--cp-ink-3)]">{t.phone}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[var(--cp-ink-3)]" />
                </li>
              ))}
              {filteredTenants.length === 0 && (
                <li className="p-4 text-center text-sm text-[var(--cp-ink-3)]">No tenants found.</li>
              )}
            </ul>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="text-sm text-[var(--cp-ink-3)] mb-2">Pick the due to mark paid</div>
            <ul className="max-h-72 overflow-y-auto divide-y divide-[var(--cp-line)]">
              {pendingDues.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between p-2 hover:bg-[var(--cp-surface-2)] cursor-pointer"
                  onClick={() => {
                    setSelectedDue(d);
                    setStep(3);
                  }}
                >
                  <div>
                    <div className="text-sm">
                      {d.property_name} · Due {fmtDate(d.due_date)}
                    </div>
                    <div className="text-xs text-[var(--cp-ink-3)]">{d.tenant_name}</div>
                  </div>
                  <div className="font-semibold">{fmtINR(d.amount_paise)}</div>
                </li>
              ))}
              {pendingDues.length === 0 && (
                <li className="p-4 text-center text-sm text-[var(--cp-ink-3)]">
                  No pending dues for this tenant.
                </li>
              )}
            </ul>
            <Button variant="outline" size="sm" onClick={() => setStep(1)} className="mt-3">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </div>
        )}

        {step === 3 && selectedDue && (
          <Step3Form
            due={selectedDue}
            onBack={() => setStep(2)}
            onSuccess={() => {
              fireConfetti();
              toast.success("Recorded");
              reset();
              onOpenChange(false);
              onSuccess();
            }}
          />
        )}
      </motion.div>
    </Dialog>
  );
}

function Stepper({ current }: { current: 1 | 2 | 3 }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {[1, 2, 3].map((n) => (
        <div
          key={n}
          className={`flex items-center gap-1 ${
            current >= n ? "text-emerald-600" : "text-[var(--cp-ink-3)]"
          }`}
        >
          {current > n ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <span className="font-bold">{n}</span>
          )}
          <span>{["Tenant", "Due", "Details"][n - 1]}</span>
          {n < 3 && <span className="mx-1">→</span>}
        </div>
      ))}
    </div>
  );
}

function Step3Form({
  due,
  onBack,
  onSuccess,
}: {
  due: PendingDue;
  onBack: () => void;
  onSuccess: () => void;
}) {
  const r = useRecordPayment({
    paymentId: due.id,
    dueAmountPaise: due.amount_paise,
    dueDate: due.due_date,
    lateFeeConfig: due.late_fee_per_day_paise
      ? {
          perDayPaise: due.late_fee_per_day_paise,
          graceDays: due.late_fee_grace_days ?? 5,
          capPaise: due.late_fee_cap_paise ?? null,
        }
      : null,
    onSuccess: () => onSuccess(),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        r.submit();
      }}
      className="space-y-3"
    >
      <SplitPaymentEditor
        value={r.splits}
        onChange={r.setSplits}
        dueAmountPaise={due.amount_paise}
      />
      <Field>
        <Label required>Paid Date</Label>
        <Input
          type="date"
          value={r.paidDate}
          onChange={(e) => r.setPaidDate(e.target.value)}
        />
      </Field>
      {r.error && <div className="text-sm text-red-500">{r.error}</div>}
      <div className="flex justify-between gap-2 border-t border-[var(--cp-line)] pt-3">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button type="submit" loading={r.busy}>
          Mark Paid + Generate Receipt
        </Button>
      </div>
    </form>
  );
}
